import { useEffect, useRef, useState } from "react";
import {
  Check,
  ChevronUp,
  Heart,
  Share2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import type { ListingOverview } from "@/types/listing";
import { cn } from "@/lib/utils";
import {
  hapticFavoriteToggle,
  hapticLightTick,
} from "@/lib/native/hapticFeedback";
import { getBileniaWebAppUrl } from "@/lib/native/openBileniaWeb";
import { buildAuctionListingPath } from "@/lib/listingUrl";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { toast } from "sonner";
import { sortEquipmentWithPriority } from "@/constants/equipmentCategories";
import {
  auctionTimeLeftLabel,
  formatKr,
  getListingImages,
  getListingModelLine,
  getListingPrice,
  getStatItems,
  getTrustChips,
} from "./forYouHelpers";
import { useForYouTheme } from "./forYouTheme";
import { useLanguage } from "@/context/LanguageContext";

type ForYouLabels = {
  directPurchase: string;
  auction: string;
  currentBid: string;
  pressForFullAd: string;
  viewFullAd: string;
  goToListing: string;
  goingToListing: string;
  addFavorite: string;
  removeFavorite: string;
  share: string;
  shared: string;
  shareFailed: string;
  accidentFree: string;
  oneOwner: string;
  owners: string;
  inspected: string;
  testDriven: string;
  year: string;
  mileage: string;
  range: string;
  transmission: string;
  fuel: string;
  annualTax: string;
  moreEquipment: string;
  daysLeft: string;
  hoursLeft: string;
  endingSoon: string;
  currencyShort: string;
};

type ForYouCardProps = {
  listing: ListingOverview;
  labels: ForYouLabels;
  layout: "mobile" | "desktop";
  onOpenDetail: () => void;
  onFavoriteToggle?: (listingId: string) => void;
  /** When the listing sheet closes, unlock gallery after last-swipe peek */
  detailOpen?: boolean;
  /** Mobile: swipe back to Alla (info section / gallery at first slide) */
  onBackToAll?: () => void;
  /** Active or adjacent card — load full-size images; others stay empty until near. */
  isActive?: boolean;
};

const TOP_EQUIPMENT_DESKTOP = 10;
const TOP_EQUIPMENT_MOBILE = 8;
/** Commit when ~1/3 of gallery width dragged (1:1 with finger) */
const PEEK_OPEN_RATIO = 0.36;
/** Fast left flick commits even with less distance */
const PEEK_FLICK_VX = 0.55; // px/ms
/** Don't steal native gallery scroll until clearly past last slide */
const PEEK_STEAL_PX = 12;
/** First-slide right swipe past this → back to Alla */
const BACK_STEAL_PX = 28;
const BACK_COMMIT_PX = 72;
/**
 * Gallery axis: bias toward horizontal — users expect left/right photo swipes.
 * Vertical still wins when clearly dominant (feed paging).
 */
const GALLERY_V_DOMINANCE = 1.55;
const GALLERY_H_DOMINANCE = 1.05;
const AXIS_LOCK_PX = 8;
/** Early V lock can flip to H if horizontal becomes clearly dominant soon. */
const GALLERY_RELOCK_H_DOMINANCE = 1.35;
const GALLERY_RELOCK_MAX_DY = 28;
/** Gallery extends this many px into the info seam so bottom swipes hit photos. */
const GALLERY_SEAM_HIT_PX = 22;
/** Info panel: prefer vertical feed scroll unless clearly horizontal (back swipe). */
const INFO_H_DOMINANCE = 1.25;
/**
 * Slow drag commit — a bit under CSS midpoint so gentle scrolls still page.
 * Flick can commit earlier with modest travel.
 */
const FEED_PAGE_RATIO = 0.32;
const FEED_FLICK_VY = 0.48;
const FEED_FLICK_MIN_RATIO = 0.08;
/** Settle duration after finger-up (TikTok-ish ease-out). */
const FEED_SETTLE_MS = 300;

export function ForYouCard({
  listing,
  labels,
  layout,
  onOpenDetail,
  onFavoriteToggle,
  detailOpen = false,
  onBackToAll,
  isActive = false,
}: ForYouCardProps) {
  const { isNative } = useNativePlatform();
  const t = useForYouTheme();
  const { language } = useLanguage();
  const galleryRef = useRef<HTMLDivElement>(null);
  const infoPanelRef = useRef<HTMLDivElement>(null);
  const slideRefs = useRef<(HTMLDivElement | null)[]>([]);
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const touchLastX = useRef<number | null>(null);
  const touchLastY = useRef<number | null>(null);
  const touchLastT = useRef<number | null>(null);
  /** Feed scrollTop at touchstart — used when vertical gesture starts on gallery/info. */
  const feedScrollStartRef = useRef(0);
  const peekVelocityRef = useRef(0); // px/ms, left swipe positive
  const feedVelocityRef = useRef(0); // px/ms, finger-down positive (scrollTop increases)
  const peekAxisLocked = useRef<"h" | "v" | null>(null);
  const infoAxisLocked = useRef<"h" | "v" | null>(null);
  const infoTouchStartX = useRef<number | null>(null);
  const infoTouchStartY = useRef<number | null>(null);
  const infoTouchLastY = useRef<number | null>(null);
  const infoTouchLastT = useRef<number | null>(null);
  const goPeekRef = useRef(0);
  const peekArmedRef = useRef(false);
  const peekCommittingRef = useRef(false);
  const [slideIndex, setSlideIndex] = useState(0);
  const [goPeek, setGoPeek] = useState(0); // 0..1 linear with finger (mobile)
  const [peekArmed, setPeekArmed] = useState(false);
  const [peekCommitting, setPeekCommitting] = useState(false);
  const setPeek = (raw: number) => {
    if (peekCommittingRef.current) return;
    // 1:1 tracking with light end resistance past ~85%
    const t = Math.max(0, Math.min(1.15, raw));
    const next = t <= 0.85 ? t : 0.85 + (t - 0.85) * 0.4;
    const clamped = Math.max(0, Math.min(1, next));
    goPeekRef.current = clamped;
    setGoPeek(clamped);
    if (clamped >= PEEK_OPEN_RATIO && !peekArmedRef.current) {
      peekArmedRef.current = true;
      setPeekArmed(true);
      hapticLightTick();
    } else if (clamped < PEEK_OPEN_RATIO * 0.72 && peekArmedRef.current) {
      peekArmedRef.current = false;
      setPeekArmed(false);
    }
  };

  const resetPeek = () => {
    goPeekRef.current = 0;
    setGoPeek(0);
    peekArmedRef.current = false;
    setPeekArmed(false);
    peekCommittingRef.current = false;
    setPeekCommitting(false);
  };

  const commitPeekOpen = () => {
    if (peekCommittingRef.current) return;
    peekCommittingRef.current = true;
    setPeekCommitting(true);
    goPeekRef.current = 1;
    setGoPeek(1);
    peekArmedRef.current = true;
    setPeekArmed(true);
    hapticLightTick();
    window.setTimeout(() => {
      onOpenDetail();
      // Reset after sheet starts opening so return feels clean
      window.setTimeout(resetPeek, 280);
    }, 220);
  };
  // Always FULL_SIZE URL; crop framing is handled via aspect/object-position below.
  const images = getListingImages(listing, { preferFullSize: true });
  const price = getListingPrice(listing);
  const isAuction = listing.listingType === "auction";
  const timeLeft = isAuction
    ? auctionTimeLeftLabel(
        listing.actualEndTime || listing.endTime,
        labels,
      )
    : null;
  const stats = getStatItems(listing, labels, language);
  const trust = getTrustChips(listing, labels);
  const equipLimit =
    layout === "mobile" ? TOP_EQUIPMENT_MOBILE : TOP_EQUIPMENT_DESKTOP;
  const rawEquipment =
    listing.vehicleDetails.equipment ??
    (listing.vehicleDetails as { equipmentPreview?: string[] }).equipmentPreview ??
    [];
  const sortedEquipment = sortEquipmentWithPriority(rawEquipment);
  const equipment = sortedEquipment.slice(0, equipLimit);
  const equipTotal =
    (listing.vehicleDetails as { equipmentTotalCount?: number })
      .equipmentTotalCount ?? sortedEquipment.length;
  const moreCount = Math.max(0, equipTotal - equipment.length);
  const modelLine = getListingModelLine(listing);
  const isFavorite = listing.isFavorite;
  const imageCount = Math.max(1, images.length);
  const onLastImage = slideIndex >= imageCount - 1;
  const onLastImageRef = useRef(onLastImage);
  onLastImageRef.current = onLastImage;
  const detailOpenRef = useRef(detailOpen);
  detailOpenRef.current = detailOpen;
  const wasDetailOpenRef = useRef(false);

  // After closing the listing sheet, unlock gallery (peek can leave scroll stuck)
  useEffect(() => {
    if (detailOpen) {
      wasDetailOpenRef.current = true;
      return;
    }
    if (!wasDetailOpenRef.current) return;
    wasDetailOpenRef.current = false;
    resetPeek();
    const el = galleryRef.current;
    if (!el || layout !== "mobile") return;
    const last = Math.max(0, imageCount - 1);
    // Re-snap to last slide after last-swipe open so right-swipe works again
    if (el.scrollLeft >= el.scrollWidth - el.clientWidth - 24 || slideIndex >= last) {
      el.scrollTo({ left: last * el.clientWidth, behavior: "auto" });
      setSlideIndex(last);
    }
    touchStartX.current = null;
    touchStartY.current = null;
    peekAxisLocked.current = null;
    peekCommittingRef.current = false;
  }, [detailOpen, imageCount, layout, slideIndex]);

  // Reliable slide index via IntersectionObserver (fixes sticky dots on mobile)
  useEffect(() => {
    const root = galleryRef.current;
    if (!root) return;
    const slides = slideRefs.current.filter(Boolean) as HTMLDivElement[];
    if (slides.length === 0) return;

    const io = new IntersectionObserver(
      (entries) => {
        let best: { idx: number; ratio: number } | null = null;
        for (const entry of entries) {
          const idx = Number((entry.target as HTMLElement).dataset.slideIndex);
          if (!Number.isFinite(idx)) continue;
          if (!best || entry.intersectionRatio > best.ratio) {
            best = { idx, ratio: entry.intersectionRatio };
          }
        }
        if (best && best.ratio > 0.45) setSlideIndex(best.idx);
      },
      { root, threshold: [0.45, 0.6, 0.75] },
    );

    slides.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [imageCount, listing.id]);

  // Last slide via scroll position (IO can lag a frame behind a fast swipe)
  const isAtLastSlide = () => {
    if (onLastImageRef.current) return true;
    const el = galleryRef.current;
    if (!el) return false;
    return el.scrollLeft + el.clientWidth >= el.scrollWidth - 12;
  };

  const isAtFirstSlide = () => {
    if (slideIndex <= 0) return true;
    const el = galleryRef.current;
    if (!el) return false;
    return el.scrollLeft <= 12;
  };
  const isAtFirstSlideRef = useRef(isAtFirstSlide);
  isAtFirstSlideRef.current = isAtFirstSlide;
  const onBackToAllRef = useRef(onBackToAll);
  onBackToAllRef.current = onBackToAll;
  const backArmedRef = useRef(false);
  const backDxRef = useRef(0);

  const getFeedScrollEl = () =>
    (galleryRef.current ?? infoPanelRef.current)?.closest(
      "[data-foryou-feed-scroll]",
    ) as HTMLElement | null;

  const driveFeedScroll = (dyFromStart: number) => {
    const feed = getFeedScrollEl();
    if (!feed) return;
    // Disable snap while dragging — otherwise mandatory snap fights 1:1 follow
    feed.style.scrollSnapType = "none";
    const max = Math.max(0, feed.scrollHeight - feed.clientHeight);
    const next = Math.max(
      0,
      Math.min(max, feedScrollStartRef.current - dyFromStart),
    );
    feed.scrollTop = next;
  };

  const settleFeedToPage = (feed: HTMLElement, targetTop: number) => {
    const startTop = feed.scrollTop;
    const dist = targetTop - startTop;
    if (Math.abs(dist) < 1.5) {
      feed.scrollTop = targetTop;
      feed.style.scrollSnapType = "";
      return;
    }
    const t0 = performance.now();
    const easeOutCubic = (t: number) => 1 - (1 - t) ** 3;
    const step = (now: number) => {
      const p = Math.min(1, (now - t0) / FEED_SETTLE_MS);
      feed.scrollTop = startTop + dist * easeOutCubic(p);
      if (p < 1) {
        requestAnimationFrame(step);
      } else {
        feed.scrollTop = targetTop;
        feed.style.scrollSnapType = "";
      }
    };
    requestAnimationFrame(step);
  };

  /** Snap feed like CSS snap-mandatory (~midpoint), with optional strong flick. */
  const snapFeedAfterVerticalDrag = () => {
    const feed = getFeedScrollEl();
    if (!feed || feed.clientHeight <= 0) return;
    const h = feed.clientHeight;
    const start = feedScrollStartRef.current;
    const delta = feed.scrollTop - start; // + = next card
    const startPage = Math.round(start / h);
    const vy = feedVelocityRef.current; // + = toward next
    const progressed = Math.abs(delta) / h;
    let page = Math.round(feed.scrollTop / h); // midpoint default

    const strongFlick =
      Math.abs(vy) >= FEED_FLICK_VY && progressed >= FEED_FLICK_MIN_RATIO;
    const draggedFar = progressed >= FEED_PAGE_RATIO;

    if (strongFlick || draggedFar) {
      if (delta > 0 || (Math.abs(delta) < 8 && vy > 0)) page = startPage + 1;
      else if (delta < 0 || (Math.abs(delta) < 8 && vy < 0)) page = startPage - 1;
    }

    const maxPage = Math.max(0, Math.ceil(feed.scrollHeight / h) - 1);
    page = Math.max(0, Math.min(maxPage, page));
    settleFeedToPage(feed, page * h);
  };

  // Non-passive touchmove on gallery (mobile):
  // - Horizontal: native gallery scroll, steal only for peek/back at edges
  // - Vertical: Android WebView won't chain to the snap feed from overflow-x,
  //   so we drive feed.scrollTop ourselves
  useEffect(() => {
    if (layout !== "mobile") return;
    const el = galleryRef.current;
    if (!el) return;
    const onMove = (e: TouchEvent) => {
      if (
        peekCommittingRef.current ||
        detailOpenRef.current ||
        touchStartX.current == null ||
        touchStartY.current == null
      ) {
        return;
      }
      const x = e.touches[0].clientX;
      const y = e.touches[0].clientY;
      const leftDx = touchStartX.current - x; // + when finger moves left
      const rightDx = x - touchStartX.current; // + when finger moves right
      const dy = y - touchStartY.current;
      const absDx = Math.abs(leftDx);
      const absDy = Math.abs(dy);

      if (!peekAxisLocked.current) {
        if (absDx < AXIS_LOCK_PX && absDy < AXIS_LOCK_PX) return;
        // Prefer H first so photo carousels feel snappy near the bottom seam.
        if (absDx >= absDy * GALLERY_H_DOMINANCE) {
          peekAxisLocked.current = "h";
        } else if (absDy >= absDx * GALLERY_V_DOMINANCE) {
          peekAxisLocked.current = "v";
        } else {
          // Still ambiguous — wait (avoid premature V lock killing gallery)
          return;
        }
      } else if (
        peekAxisLocked.current === "v" &&
        absDy < GALLERY_RELOCK_MAX_DY &&
        absDx >= absDy * GALLERY_RELOCK_H_DOMINANCE
      ) {
        // Soft V lock from finger wobble → recover into gallery swipe
        peekAxisLocked.current = "h";
      }

      if (peekAxisLocked.current === "v") {
        // Web: leave vertical to CSS snap (felt correct before native JS drive).
        // Native WebView: overflow-x gallery won't chain — drive feed ourselves.
        if (!isNative) return;
        e.preventDefault();
        const now = performance.now();
        if (touchLastT.current != null && touchLastY.current != null) {
          const dt = now - touchLastT.current;
          if (dt > 0) {
            // Finger down → scrollTop up: velocity positive when dy decreases
            feedVelocityRef.current = (touchLastY.current - y) / dt;
          }
        }
        touchLastY.current = y;
        touchLastT.current = now;
        driveFeedScroll(dy);
        return;
      }

      const now = performance.now();
      if (touchLastX.current != null && touchLastT.current != null) {
        const dt = now - touchLastT.current;
        if (dt > 0) {
          peekVelocityRef.current = (touchLastX.current - x) / dt;
        }
      }
      touchLastX.current = x;
      touchLastT.current = now;

      // First slide + swipe right → back to Alla
      if (isAtFirstSlideRef.current() && rightDx > BACK_STEAL_PX && leftDx < 0) {
        e.preventDefault();
        backArmedRef.current = true;
        backDxRef.current = rightDx;
        if (goPeekRef.current > 0) setPeek(0);
        return;
      }
      backArmedRef.current = false;
      backDxRef.current = 0;

      // Last slide + swipe left → peek open ad
      if (!isAtLastSlide()) return;
      if (leftDx <= PEEK_STEAL_PX) {
        if (goPeekRef.current > 0) setPeek(0);
        return;
      }
      e.preventDefault();
      const w = el.clientWidth || 1;
      setPeek(leftDx / w);
    };
    el.addEventListener("touchmove", onMove, { passive: false });
    return () => el.removeEventListener("touchmove", onMove);
  }, [layout, isNative]);

  // Info panel: JS feed drive only on native WebView (Android won't chain
  // vertical from a non-scrolling child). On web, keep CSS snap — it already
  // felt TikTok-correct before we stole the gesture.
  useEffect(() => {
    if (layout !== "mobile" || !isNative) return;
    const el = infoPanelRef.current;
    if (!el) return;

    const onStart = (e: TouchEvent) => {
      if (peekCommittingRef.current || detailOpenRef.current) return;
      infoTouchStartX.current = e.touches[0].clientX;
      infoTouchStartY.current = e.touches[0].clientY;
      infoTouchLastY.current = e.touches[0].clientY;
      infoTouchLastT.current = performance.now();
      feedScrollStartRef.current = getFeedScrollEl()?.scrollTop ?? 0;
      feedVelocityRef.current = 0;
      infoAxisLocked.current = null;
    };

    const onMove = (e: TouchEvent) => {
      if (
        peekCommittingRef.current ||
        detailOpenRef.current ||
        infoTouchStartX.current == null ||
        infoTouchStartY.current == null
      ) {
        return;
      }
      const x = e.touches[0].clientX;
      const y = e.touches[0].clientY;
      const dx = x - infoTouchStartX.current;
      const dy = y - infoTouchStartY.current;
      const absDx = Math.abs(dx);
      const absDy = Math.abs(dy);

      if (!infoAxisLocked.current) {
        if (absDx < AXIS_LOCK_PX && absDy < AXIS_LOCK_PX) return;
        if (absDx >= absDy * INFO_H_DOMINANCE) {
          infoAxisLocked.current = "h";
        } else if (absDy >= absDx * 0.9) {
          infoAxisLocked.current = "v";
        } else {
          return;
        }
      }

      if (infoAxisLocked.current === "v") {
        e.preventDefault();
        const now = performance.now();
        if (infoTouchLastT.current != null && infoTouchLastY.current != null) {
          const dt = now - infoTouchLastT.current;
          if (dt > 0) {
            feedVelocityRef.current = (infoTouchLastY.current - y) / dt;
          }
        }
        infoTouchLastY.current = y;
        infoTouchLastT.current = now;
        driveFeedScroll(dy);
      }
    };

    const onEnd = () => {
      if (infoAxisLocked.current === "v") {
        snapFeedAfterVerticalDrag();
      }
      infoTouchStartX.current = null;
      infoTouchStartY.current = null;
      infoTouchLastY.current = null;
      infoTouchLastT.current = null;
      infoAxisLocked.current = null;
      feedVelocityRef.current = 0;
    };

    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchmove", onMove, { passive: false });
    el.addEventListener("touchend", onEnd);
    el.addEventListener("touchcancel", onEnd);
    return () => {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchmove", onMove);
      el.removeEventListener("touchend", onEnd);
      el.removeEventListener("touchcancel", onEnd);
    };
  }, [layout, isNative]);

  const scrollGallery = (dir: -1 | 1) => {
    const el = galleryRef.current;
    if (!el) return;
    const next = Math.max(0, Math.min(imageCount - 1, slideIndex + dir));
    setSlideIndex(next);
    el.scrollTo({ left: next * el.clientWidth, behavior: "smooth" });
  };

  const handleFavorite = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    hapticFavoriteToggle();
    onFavoriteToggle?.(listing.id);
  };

  const handleShare = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const path =
      listing.campaignId != null
        ? buildAuctionListingPath(listing.campaignId, listing)
        : `/auctions`;
    const url = getBileniaWebAppUrl(path);
    try {
      if (isNative) {
        const { Share } = await import("@capacitor/share");
        const canShare = await Share.canShare();
        if (canShare.value) {
          await Share.share({
            title: listing.title,
            url,
            dialogTitle: labels.share,
          });
          toast.success(labels.shared);
          return;
        }
      }
      if (navigator.share) {
        await navigator.share({ title: listing.title, url });
      } else {
        await navigator.clipboard.writeText(url);
      }
      toast.success(labels.shared);
    } catch (err) {
      if (
        err instanceof Error &&
        (err.name === "AbortError" || err.message?.includes("cancel"))
      ) {
        return;
      }
      toast.error(labels.shareFailed);
    }
  };

  const statusLabel = isAuction
    ? timeLeft
      ? `${labels.auction} · ${timeLeft}`
      : labels.auction
    : labels.directPurchase;

  // Swipe past last image → page-push panel from the right (mobile)
  const onGalleryTouchStart = (e: React.TouchEvent) => {
    if (layout !== "mobile" || peekCommittingRef.current || detailOpen) return;
    // Clear stale peek from a prior open so gallery isn't stuck
    if (goPeekRef.current > 0) resetPeek();
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    touchLastX.current = e.touches[0].clientX;
    touchLastY.current = e.touches[0].clientY;
    touchLastT.current = performance.now();
    feedScrollStartRef.current = getFeedScrollEl()?.scrollTop ?? 0;
    peekVelocityRef.current = 0;
    feedVelocityRef.current = 0;
    peekAxisLocked.current = null;
  };
  const onGalleryTouchEnd = () => {
    if (layout !== "mobile") return;
    const axis = peekAxisLocked.current;
    if (peekCommittingRef.current || detailOpenRef.current) {
      touchStartX.current = null;
      touchStartY.current = null;
      touchLastY.current = null;
      backArmedRef.current = false;
      backDxRef.current = 0;
      peekAxisLocked.current = null;
      return;
    }

    // Snap feed after a vertical drag that started on the gallery (native only)
    if (axis === "v") {
      if (isNative) snapFeedAfterVerticalDrag();
      touchStartX.current = null;
      touchStartY.current = null;
      touchLastX.current = null;
      touchLastY.current = null;
      touchLastT.current = null;
      peekVelocityRef.current = 0;
      feedVelocityRef.current = 0;
      peekAxisLocked.current = null;
      backArmedRef.current = false;
      backDxRef.current = 0;
      return;
    }

    // First-slide right swipe → Alla annonser
    if (
      backArmedRef.current &&
      backDxRef.current >= BACK_COMMIT_PX &&
      onBackToAllRef.current
    ) {
      backArmedRef.current = false;
      backDxRef.current = 0;
      touchStartX.current = null;
      touchStartY.current = null;
      touchLastX.current = null;
      touchLastT.current = null;
      peekVelocityRef.current = 0;
      peekAxisLocked.current = null;
      resetPeek();
      onBackToAllRef.current();
      return;
    }
    backArmedRef.current = false;
    backDxRef.current = 0;

    const vx = peekVelocityRef.current;
    const shouldOpen =
      goPeekRef.current >= PEEK_OPEN_RATIO ||
      (goPeekRef.current > 0.12 && vx >= PEEK_FLICK_VX);

    if (shouldOpen) commitPeekOpen();
    else resetPeek();

    touchStartX.current = null;
    touchStartY.current = null;
    touchLastX.current = null;
    touchLastT.current = null;
    peekVelocityRef.current = 0;
    peekAxisLocked.current = null;
  };

  const galleryDots = images.length > 1 && (layout === "desktop" || goPeek < 0.15) && (
    <div
      className={cn(
        "pointer-events-none absolute z-40 flex items-center justify-center gap-1.5 transition-opacity duration-150",
        layout === "desktop" ? "inset-x-0 bottom-5" : "inset-x-0",
      )}
      style={{
        // Mobile: sit above the info seam so the solid info panel never clips the dots.
        ...(layout === "mobile"
          ? { bottom: GALLERY_SEAM_HIT_PX + 10 }
          : undefined),
        ...(layout === "mobile" && goPeek > 0.05
          ? { opacity: Math.max(0, 1 - goPeek * 2.2) }
          : undefined),
      }}
    >
      {images.map((_, i) => (
        <i
          key={i}
          className={cn(
            "rounded-full transition-all duration-200",
            t.dark
              ? "shadow-[0_0_6px_rgba(0,0,0,0.45)]"
              : "shadow-[0_0_6px_rgba(255,255,255,0.55)] ring-1 ring-white/50",
            i === slideIndex
              ? cn(
                  layout === "desktop" ? "h-1.5 w-5" : "h-2 w-2",
                  t.galleryDotActive,
                )
              : cn("h-1.5 w-1.5", t.galleryDotIdle),
          )}
        />
      ))}
    </div>
  );

  const railBtnClass = cn(
    "pointer-events-auto grid h-11 w-11 place-items-center rounded-full ring-1 backdrop-blur-md outline-none focus:outline-none focus-visible:ring-sky-400/40",
    t.railBtn,
  );

  const rail = (layout === "desktop" || goPeek < 0.2) && (
    <div
      className={cn(
        "absolute z-30 flex flex-col gap-3.5 pointer-events-none transition-opacity duration-150",
        // Desktop: higher so it doesn't collide with gallery ←→ chevrons
        layout === "mobile" ? "right-3 top-[28%]" : "right-3 top-14",
      )}
      style={
        layout === "mobile" && goPeek > 0.04
          ? { opacity: Math.max(0, 1 - goPeek * 3) }
          : undefined
      }
    >
      <button
        type="button"
        onClick={handleFavorite}
        aria-label={isFavorite ? labels.removeFavorite : labels.addFavorite}
        className={railBtnClass}
      >
        <Heart
          className={cn(
            "h-[22px] w-[22px] transition",
            isFavorite
              ? "fill-[#FF5C6C] text-[#FF5C6C]"
              : "text-[#FF7A8A] fill-[#FF7A8A]/25",
          )}
        />
      </button>
      <button
        type="button"
        onClick={handleShare}
        aria-label={labels.share}
        className={railBtnClass}
      >
        <Share2 className="h-[22px] w-[22px] text-sky-400" />
      </button>
    </div>
  );

  const infoBody = (
    <div
      className={cn(
        "flex flex-col",
        layout === "mobile" ? "gap-1" : "gap-0",
      )}
    >
      {/* Price + listing type — type pill pinned right on mobile */}
      <div
        className={cn(
          "flex items-center gap-x-3 gap-y-1",
          layout === "mobile" ? "justify-between" : "flex-wrap gap-x-4 gap-y-2",
        )}
      >
        <div
          className={cn(
            "min-w-0 font-bold tabular-nums tracking-tight leading-none",
            t.price,
            layout === "desktop"
              ? "text-[30px] xl:text-[36px]"
              : "whitespace-nowrap text-[24px] min-[390px]:text-[26px]",
          )}
        >
          {formatKr(price)}
          <small
            className={cn(
              "ml-1 font-semibold",
              layout === "desktop" ? "text-[15px] xl:text-[17px]" : "text-[13px]",
              t.priceSuffix,
            )}
          >
            {labels.currencyShort}
          </small>
        </div>
        <span
          className={cn(
            "inline-flex shrink-0 items-center justify-center gap-1.5 self-center whitespace-nowrap rounded-full font-semibold leading-none text-white",
            layout === "desktop"
              ? "ml-1 px-2.5 py-1 text-[11px]"
              : "px-2 py-0.5 text-[10px]",
            isAuction ? "bg-[#A8650F]" : "bg-[#1B8F5A]",
          )}
        >
          <span
            className={cn(
              "rounded-full bg-white",
              layout === "desktop" ? "h-1.5 w-1.5" : "h-1 w-1",
            )}
          />
          {statusLabel}
        </span>
      </div>

      <div className={cn(layout === "desktop" ? "mt-3" : "mt-0.5")}>
        <h3
          className={cn(
            "font-semibold tracking-tight leading-snug line-clamp-2",
            t.title,
            layout === "desktop" ? "text-[22px]" : "text-[15px]",
          )}
        >
          {listing.title}
        </h3>
        {modelLine &&
          modelLine.toLowerCase() !== listing.title.trim().toLowerCase() && (
            <p
              className={cn(
                "mt-0.5 line-clamp-1",
                t.subtitle,
                layout === "desktop" ? "text-[15px]" : "text-[12px]",
              )}
            >
              {modelLine}
            </p>
          )}
      </div>

      {stats.length > 0 && (
        <div
          className={cn(
            "grid grid-cols-4",
            layout === "desktop" ? "mt-4" : "mt-0.5",
          )}
        >
          {stats.map((s, i) => (
            <div
              key={s.label}
              className={cn(
                "min-w-0 px-0 pl-2",
                i > 0 && cn("border-l", t.statBorder),
                i === 0 && "pl-0",
              )}
            >
              <b
                className={cn(
                  "block font-semibold tabular-nums tracking-tight",
                  t.statValue,
                  layout === "desktop"
                    ? "whitespace-nowrap text-[14px] xl:text-[15px]"
                    : "truncate text-[12px]",
                )}
              >
                {layout === "desktop" && s.compactValue
                  ? s.compactValue
                  : s.value}
              </b>
              <span
                className={cn(
                  "block truncate",
                  t.statLabel,
                  layout === "desktop" ? "text-[11px]" : "text-[9.5px]",
                )}
              >
                {s.label}
              </span>
            </div>
          ))}
        </div>
      )}

      {trust.length > 0 && (
        <div
          className={cn(
            "flex flex-wrap",
            layout === "desktop" ? "mt-3.5 gap-1.5" : "mt-1 gap-1",
          )}
        >
          {trust.map((chip) => (
            <div
              key={chip.key}
              className={cn(
                "inline-flex max-w-full items-center justify-center gap-0.5 rounded-[10px] border border-[#3DDC97]/30 bg-[#3DDC97]/10 font-semibold",
                layout === "desktop"
                  ? "px-2.5 py-1.5 text-[11px]"
                  : "px-2 py-1 text-[10px]",
              )}
            >
              <Check
                className={cn(
                  "shrink-0 stroke-[#3DDC97] stroke-[2.8]",
                  layout === "desktop" ? "h-3 w-3" : "h-2.5 w-2.5",
                )}
              />
              <span className="truncate">{chip.label}</span>
            </div>
          ))}
        </div>
      )}

      {(equipment.length > 0 || moreCount > 0) && (
        <div
          className={cn(
            "flex flex-wrap",
            layout === "desktop" ? "mt-4 gap-1.5" : "mt-1 gap-1",
          )}
        >
          {equipment.map((eq) => (
            <span
              key={eq}
              className={cn(
                "inline-flex items-center rounded-[10px] font-medium leading-none",
                layout === "desktop"
                  ? "px-2.5 py-1.5 text-[12.5px]"
                  : "px-2.5 py-1.5 text-[11px]",
                t.equipmentPill,
              )}
            >
              {eq}
            </span>
          ))}
          {moreCount > 0 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onOpenDetail();
              }}
              className={cn(
                "font-medium underline-offset-[3px]",
                layout === "desktop"
                  ? "px-1 py-1.5 text-[12.5px]"
                  : "px-1 py-1 text-[11px]",
                t.moreEquip,
              )}
            >
              {labels.moreEquipment.replace("{count}", String(moreCount))}
            </button>
          )}
        </div>
      )}

    </div>
  );

  const peekTransition =
    goPeek === 0 || peekCommitting
      ? "transform 300ms cubic-bezier(.22,1,.28,1), opacity 260ms ease"
      : "none";
  const mobilePeek = layout === "mobile" ? goPeek : 0;

  const gallerySlides = (
    <div
      ref={galleryRef}
      className={cn(
        "relative flex w-full snap-x snap-mandatory overflow-x-auto overscroll-x-none scrollbar-none",
        layout === "desktop" ? "absolute inset-0 h-full" : "h-full",
      )}
      style={{
        scrollbarWidth: "none",
        // Native: pan-x only so browser commits to gallery swipes; JS drives feed.
        // Web: keep pan-y so CSS snap feed still chains from the photo band.
        touchAction: isNative ? "pan-x" : "pan-x pan-y",
      }}
      onTouchStart={onGalleryTouchStart}
      onTouchEnd={onGalleryTouchEnd}
      onTouchCancel={onGalleryTouchEnd}
    >
      {(images.length ? images : [""]).map((src, i) => (
        <div
          key={i}
          ref={(el) => {
            slideRefs.current[i] = el;
          }}
          data-slide-index={i}
          className={cn(
            "relative w-full shrink-0 snap-center",
            t.slideBg,
            layout === "desktop" ? "h-full" : undefined,
          )}
          style={
            layout === "mobile"
              ? {
                  // Keep photo framing at the aspect band; bottom seam is touch-only.
                  height: `calc(100% - ${GALLERY_SEAM_HIT_PX}px)`,
                }
              : undefined
          }
        >
          {src && isActive ? (
            <img
              src={src}
              alt=""
              draggable={false}
              loading={i === 0 ? "eager" : "lazy"}
              decoding="async"
              className={cn(
                // Full car visible (letterbox) — FULL_SIZE URL, no aggressive crop
                "h-full w-full select-none object-contain object-center",
              )}
            />
          ) : src ? (
            <div
              className={cn("h-full w-full", t.slideBg)}
              aria-hidden
            />
          ) : (
            <div
              className={cn(
                "flex h-full w-full items-center justify-center text-sm",
                t.slideBg,
                t.empty,
              )}
            >
              —
            </div>
          )}
        </div>
      ))}
    </div>
  );

  // Must sit OUTSIDE the horizontal scroller — absolute inside overflow-x
  // sticks to scroll origin (first slide), so the hint vanished on last image.
  const mobilePeekOverlay = layout === "mobile" && mobilePeek > 0.01 && (
    <div
      className="pointer-events-none absolute inset-0 z-20"
      style={{ transition: peekTransition }}
    >
      {/* Dim the whole photo under the swipe */}
      <div
        className={cn("absolute inset-0", t.peekVeil)}
        style={{
          opacity: Math.min(0.72, 0.18 + mobilePeek * 0.62),
          transition: peekTransition,
        }}
      />
      <div
        className="absolute inset-y-0 right-0 flex w-[78%] flex-col items-center justify-center gap-2 px-5 text-center"
        style={{
          transform: `translate3d(${(1 - mobilePeek) * 28}%, 0, 0)`,
          // Visible from the first pixels of the swipe
          opacity: Math.min(1, 0.25 + mobilePeek * 1.5),
          transition: peekTransition,
        }}
      >
        <div
          className={cn(
            "flex h-12 w-12 items-center justify-center rounded-full ring-1 backdrop-blur-md transition-transform duration-200",
            t.dark
              ? "bg-white/14 ring-white/35"
              : "bg-white/80 ring-black/10 shadow-md",
            peekArmed && (t.dark ? "scale-105 bg-white/20" : "scale-105 bg-white"),
          )}
        >
          <ChevronRight
            className={cn("h-6 w-6", t.dark ? "text-white" : "text-foreground")}
            strokeWidth={2.4}
          />
        </div>
        <span
          className={cn(
            "text-[15px] font-medium tracking-tight drop-shadow",
            t.dark ? "text-white" : "text-foreground",
          )}
        >
          {labels.goingToListing}
        </span>
      </div>
    </div>
  );

  const galleryNavBtn = cn(
    "absolute top-1/2 z-20 grid h-11 w-8 -translate-y-1/2 place-items-center rounded-xl ring-1 backdrop-blur-md outline-none focus:outline-none focus-visible:ring-sky-400/40 border-0",
    t.galleryNav,
  );

  if (layout === "desktop") {
    return (
      <article
        className={cn(
          "relative grid h-full w-full gap-4 overflow-hidden px-5 py-5 xl:gap-5 xl:px-8 xl:py-6",
          t.article,
        )}
        style={{
          gridTemplateColumns: "minmax(0, 1fr) minmax(360px, 420px)",
        }}
      >
        {/* Image island — fixed height so odd ratios don’t leave empty dead space */}
        <div
          className={cn(
            "relative z-0 flex h-[min(75%,42rem)] min-h-0 min-w-0 w-[75%] items-center justify-center justify-self-center self-center overflow-hidden rounded-[28px]",
            t.imageIsland,
          )}
        >
          {gallerySlides}
          {/* Top fade only on the image — never across the info column */}
          <div
            className="pointer-events-none absolute inset-x-0 top-0 z-10 h-36"
            style={{ background: t.imageFadeTop }}
          />
          {galleryDots}
          {images.length > 1 && (
            <>
              <button
                type="button"
                aria-label="Prev"
                onClick={(e) => {
                  e.stopPropagation();
                  scrollGallery(-1);
                }}
                className={cn(galleryNavBtn, "left-3")}
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              {!onLastImage && (
                <button
                  type="button"
                  aria-label="Next"
                  onClick={(e) => {
                    e.stopPropagation();
                    scrollGallery(1);
                  }}
                  className={cn(galleryNavBtn, "right-3")}
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              )}
            </>
          )}
          {rail}
        </div>

        {/* Info island — solid opaque panel, no overlay bleed */}
        <div
          className={cn(
            "relative z-20 flex min-h-0 w-full flex-col justify-center overflow-y-auto rounded-[28px] px-7 py-8 pr-14 xl:px-9 xl:pr-16",
            t.infoIsland,
          )}
        >
          <div className="relative z-10">{infoBody}</div>
          <button
            type="button"
            onClick={onOpenDetail}
            className={cn(
              "relative z-10 mt-8 flex w-full items-center justify-center gap-2 rounded-2xl px-4 py-3.5 text-[15px] font-semibold transition active:scale-[0.99]",
              t.ctaDesktop,
            )}
          >
            <ChevronRight className="h-4 w-4" />
            <span>{labels.viewFullAd}</span>
          </button>
        </div>
      </article>
    );
  }

  // Mobile: ~4:3 band + object-contain so FULL_SIZE exterior shots show the whole car
  return (
    <article
      className={cn(
        "relative flex h-full w-full flex-col overflow-hidden",
        t.articleMobile,
      )}
    >
      <div
        className={cn(
          "relative w-full shrink-0",
          // Native: no browser chrome → give the photo band more of the viewport.
          isNative
            ? "h-[52%] min-h-[280px] max-h-[62%]"
            : "aspect-[4/3] max-h-[56%]",
          t.slideBg,
        )}
      >
        {/*
          Gallery scroller taller than the photo band by GALLERY_SEAM_HIT_PX:
          slides keep aspect height; transparent seam catches swipes near dots
          while the bottom fade blends photo into info (prod look).
        */}
        <div
          className="absolute inset-x-0 top-0 z-10"
          style={{
            height: `calc(100% + ${GALLERY_SEAM_HIT_PX}px)`,
            opacity: 1 - mobilePeek * 0.35,
            filter:
              mobilePeek > 0.02
                ? `brightness(${1 - mobilePeek * 0.35})`
                : undefined,
            transition: peekTransition,
          }}
        >
          {gallerySlides}
        </div>
        <div
          className="pointer-events-none absolute inset-x-0 top-0 z-[15] h-20"
          style={{ background: t.imageFadeTop }}
        />
        {galleryDots}
        {rail}
        {mobilePeekOverlay}
        {/*
          Photo→info fade ON TOP of gallery (portrait letterbox included).
          Seam stays transparent on the info panel so this ramp stays visible;
          price/text sit above at z-30.
        */}
        <div
          className="pointer-events-none absolute inset-x-0 z-20"
          style={{
            bottom: -GALLERY_SEAM_HIT_PX,
            height: 96 + GALLERY_SEAM_HIT_PX,
            background: t.imageFadeBottom,
          }}
        />
      </div>

      <div
        data-foryou-info=""
        className="pointer-events-none relative z-30 flex min-h-0 flex-1 flex-col px-4 pt-1.5"
        style={{
          // Pull into seam; top of panel is transparent so photo fade shows through.
          marginTop: -GALLERY_SEAM_HIT_PX,
          paddingTop: GALLERY_SEAM_HIT_PX + 8,
          paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 8px)",
          background: `linear-gradient(to bottom, transparent 0px, transparent ${GALLERY_SEAM_HIT_PX}px, ${t.infoMobileSolid} ${GALLERY_SEAM_HIT_PX}px)`,
          opacity: 1 - mobilePeek * 0.72,
          filter: mobilePeek > 0.02 ? `blur(${mobilePeek * 2.5}px)` : undefined,
          transform: `translate3d(${mobilePeek * -6}%, 0, 0)`,
          transition: peekTransition,
        }}
      >
        <div
          ref={infoPanelRef}
          // No overflow-y-auto: nested vertical scroll blocks swipe-to-previous
          // on the snap feed (Android). JS drives feed.scrollTop (same as gallery).
          className="pointer-events-auto flex min-h-0 flex-1 flex-col overflow-hidden touch-pan-y"
          onPointerDown={
            onBackToAll
              ? (e) => {
                  (e.currentTarget as HTMLElement).dataset.backStartX = String(
                    e.clientX,
                  );
                  (e.currentTarget as HTMLElement).dataset.backStartY = String(
                    e.clientY,
                  );
                }
              : undefined
          }
          onPointerUp={
            onBackToAll
              ? (e) => {
                  // Skip back-swipe if this gesture was a vertical feed scroll
                  if (infoAxisLocked.current === "v") return;
                  const startX = Number(
                    (e.currentTarget as HTMLElement).dataset.backStartX,
                  );
                  const startY = Number(
                    (e.currentTarget as HTMLElement).dataset.backStartY,
                  );
                  if (!Number.isFinite(startX) || !Number.isFinite(startY))
                    return;
                  const dx = e.clientX - startX;
                  const dy = e.clientY - startY;
                  // Swipe right on info → back to Alla
                  if (
                    dx > BACK_COMMIT_PX &&
                    Math.abs(dx) > Math.abs(dy) * 1.2
                  ) {
                    onBackToAll();
                  }
                }
              : undefined
          }
        >
          <div className="flex flex-1 flex-col justify-start gap-0">
            {infoBody}
          </div>
          <button
            type="button"
            onClick={onOpenDetail}
            className="mt-auto w-full shrink-0 pb-0.5 pt-2"
          >
            <div
              className={cn(
                "flex flex-col items-center gap-0.5 text-[11.5px]",
                t.ctaMobile,
              )}
            >
              <ChevronUp className={cn("h-4 w-4", t.ctaMobileIcon)} />
              <span className={cn("font-medium", t.ctaMobileLabel)}>
                {labels.pressForFullAd}
              </span>
            </div>
          </button>
        </div>
      </div>
    </article>
  );
}
